import { useState } from 'react';
import { Brain, CheckCircle, ExternalLink, ShieldAlert, Bell, Loader2, Gauge, BarChart3, Edit3, Undo2 } from 'lucide-react';
import { ReasoningDisplay } from './ReasoningDisplay';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Decision } from '@/types';
import { canIframe, formatRelativeTime } from '@/utils/formatting';
import { api } from '@/services/api';
import { cn } from '@/lib/utils';
import { useAuth } from '@/contexts/AuthContext';

interface EventModalProps {
  decision: Decision | null;
  onClose: () => void;
  onAlertSent?: (decisionId: string, lastAlertedAt: string) => void;
  onDecisionUpdated?: (decision: Decision) => void;
}

export function EventModal({ decision, onClose, onAlertSent, onDecisionUpdated }: EventModalProps) {
  const { hasRole } = useAuth();
  const [alerting, setAlerting] = useState(false);
  const [alertError, setAlertError] = useState<string | null>(null);
  const [localLastAlerted, setLocalLastAlerted] = useState<string | null>(null);

  // Override state
  const [showOverride, setShowOverride] = useState(false);
  const [overrideScore, setOverrideScore] = useState(50);
  const [overrideReason, setOverrideReason] = useState('');
  const [overriding, setOverriding] = useState(false);
  const [overrideError, setOverrideError] = useState<string | null>(null);

  if (!decision) return null;

  const lastAlertedAt = localLastAlerted || decision.last_alerted_at;
  const isOverridden = decision.override_score != null;
  const canOverride = hasRole('reviewer');

  const handleTriggerAlert = async () => {
    setAlerting(true);
    setAlertError(null);
    try {
      const result = await api.triggerAlert(decision.id);
      setLocalLastAlerted(result.last_alerted_at);
      onAlertSent?.(decision.id, result.last_alerted_at);
    } catch (err) {
      setAlertError(err instanceof Error ? err.message : 'Failed to send alert');
    } finally {
      setAlerting(false);
    }
  };

  const handleOverride = async () => {
    if (!overrideReason.trim()) {
      setOverrideError('A reason is required for overrides');
      return;
    }
    setOverriding(true);
    setOverrideError(null);
    try {
      const result = await api.overrideDecision(decision.id, overrideScore / 100, overrideReason);
      // Update the decision in-place
      const updated: Decision = {
        ...decision,
        relevance_score: result.new_score,
        severity: result.new_severity as Decision['severity'],
        is_relevant: result.new_is_relevant,
        original_score: result.original_score,
        override_score: result.new_score,
        override_reason: overrideReason,
        overridden_at: new Date().toISOString(),
      };
      onDecisionUpdated?.(updated);
      setShowOverride(false);
      setOverrideReason('');
    } catch (err) {
      setOverrideError(err instanceof Error ? err.message : 'Failed to apply override');
    } finally {
      setOverriding(false);
    }
  };

  const scorePercent = Math.round(decision.relevance_score * 100);
  const scoreColor = scorePercent >= 70
    ? 'from-green-500 to-emerald-500'
    : scorePercent >= 50
      ? 'from-sabio-yellow to-amber-500'
      : 'from-gray-400 to-gray-500';
  const scoreLabel = scorePercent >= 70
    ? 'Highly Relevant'
    : scorePercent >= 50
      ? 'Moderately Relevant'
      : 'Low Relevance';
  const scoreLabelColor = scorePercent >= 70
    ? 'text-green-600 dark:text-green-400'
    : scorePercent >= 50
      ? 'text-sabio-yellow'
      : 'text-muted-foreground';

  const canShowIframe = canIframe(decision.event_url);

  // Confidence helpers
  const confidence = decision.confidence;
  const confidencePercent = confidence != null ? Math.round(confidence * 100) : null;
  const confidenceColor = confidencePercent != null
    ? confidencePercent >= 80
      ? 'text-green-500'
      : confidencePercent >= 60
        ? 'text-sabio-yellow'
        : confidencePercent >= 40
          ? 'text-orange-500'
          : 'text-red-500'
    : '';

  return (
    <Dialog open={!!decision} onOpenChange={() => onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center gap-1.5 mb-2 flex-wrap">
            {decision.is_relevant ? (
              <Badge variant="subtle-green" className="text-[10px]">&#10003; RELEVANT</Badge>
            ) : (
              <Badge variant="secondary" className="text-[10px]">filtered</Badge>
            )}
            <Badge variant={decision.severity as any} className="text-[10px]">
              {decision.severity}
            </Badge>
            <Badge variant="subtle-purple" className="text-[10px]">
              {decision.event_source}
            </Badge>
            {isOverridden && (
              <Badge variant="outline" className="text-[10px] border-orange-500 text-orange-500">
                <Edit3 className="w-3 h-3 mr-0.5" /> overridden
              </Badge>
            )}
          </div>
          <DialogTitle className="text-xl leading-tight">{decision.event_title}</DialogTitle>
        </DialogHeader>

        <div className="space-y-5 mt-2">
          {/* Relevance Score + Confidence */}
          <div className="flex items-center gap-4 p-4 rounded-xl bg-muted/50 border">
            <div className={cn(
              'w-16 h-16 rounded-xl flex items-center justify-center shadow-md bg-gradient-to-br',
              scoreColor
            )}>
              <span className="text-2xl font-bold text-white">{scorePercent}</span>
            </div>
            <div className="flex-1">
              <p className="text-xs text-muted-foreground uppercase tracking-wider mb-0.5">Relevance Score</p>
              <p className={cn('text-lg font-semibold', scoreLabelColor)}>{scoreLabel}</p>
              {isOverridden && decision.original_score != null && (
                <p className="text-xs text-muted-foreground mt-0.5">
                  Original: {Math.round(decision.original_score * 100)}%
                  {decision.override_reason && <> &mdash; "{decision.override_reason}"</>}
                </p>
              )}
            </div>
            {confidencePercent != null && (
              <div className="text-center">
                <div className="flex items-center gap-1 mb-0.5">
                  <Gauge className={cn('w-4 h-4', confidenceColor)} />
                  <span className={cn('text-lg font-bold', confidenceColor)}>{confidencePercent}%</span>
                </div>
                <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Confidence</p>
              </div>
            )}
          </div>

          {/* Confidence reason */}
          {decision.confidence_reason && (
            <p className="text-sm text-muted-foreground italic px-1">
              <Gauge className="w-3.5 h-3.5 inline mr-1 opacity-60" />
              {decision.confidence_reason}
            </p>
          )}

          {/* Scoring Breakdown */}
          {decision.scoring_breakdown && (
            <>
              <Separator />
              <div>
                <h3 className="text-xs text-muted-foreground uppercase tracking-wider mb-3 flex items-center gap-2">
                  <BarChart3 className="w-4 h-4 text-sabio-yellow" />
                  Scoring Breakdown
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <FactorBar label="Call Likelihood" value={decision.scoring_breakdown.call_likelihood} weight="30%" />
                  <FactorBar label="Scale of Impact" value={decision.scoring_breakdown.scale_of_impact} weight="25%" />
                  <FactorBar label="Imminence" value={decision.scoring_breakdown.imminence} weight="25%" />
                  <FactorBar label="Geographic Match" value={decision.scoring_breakdown.geographic_match} weight="10%" />
                  <FactorBar label="Actionability" value={decision.scoring_breakdown.actionability} weight="10%" />
                </div>
                <div className="flex gap-4 mt-3 text-xs text-muted-foreground">
                  {decision.scoring_breakdown.hub_multiplier !== 1 && (
                    <span>Hub: &times;{decision.scoring_breakdown.hub_multiplier.toFixed(2)}</span>
                  )}
                  {decision.scoring_breakdown.imminence_multiplier !== 1 && (
                    <span>Imminence: &times;{decision.scoring_breakdown.imminence_multiplier.toFixed(2)}</span>
                  )}
                  {decision.scoring_breakdown.estimated_call_impact && (
                    <span className="capitalize">Call impact: {decision.scoring_breakdown.estimated_call_impact}</span>
                  )}
                </div>
                {decision.scoring_breakdown.affected_touchpoints?.length > 0 && (
                  <div className="flex gap-1 flex-wrap mt-2">
                    {decision.scoring_breakdown.affected_touchpoints.map(tp => (
                      <Badge key={tp} variant="secondary" className="text-[10px]">
                        {tp.replace(/_/g, ' ')}
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}

          <Separator />

          {/* Reasoning */}
          <div>
            <h3 className="text-xs text-muted-foreground uppercase tracking-wider mb-2 flex items-center gap-2">
              <Brain className="w-4 h-4 text-sabio-yellow" />
              Why This Is Relevant
            </h3>
            <div className="p-4 rounded-xl bg-muted/30 border">
              <ReasoningDisplay reasoning={decision.reasoning} />
            </div>
          </div>

          {/* Matched Criteria */}
          {decision.matched_criteria?.length > 0 && (
            <div>
              <h3 className="text-xs text-muted-foreground uppercase tracking-wider mb-2 flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-green-500" />
                Matched Business Criteria
              </h3>
              <div className="flex gap-1.5 flex-wrap">
                {decision.matched_criteria.map(c => (
                  <Badge key={c} variant="subtle-purple">{c}</Badge>
                ))}
              </div>
            </div>
          )}

          {/* Override Section */}
          {canOverride && (
            <div className="p-4 rounded-xl bg-muted/50 border">
              {!showOverride ? (
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-xs text-muted-foreground uppercase tracking-wider mb-1 flex items-center gap-2">
                      <Edit3 className="w-4 h-4 text-orange-500" />
                      Score Override
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      {isOverridden ? 'This decision has been overridden.' : 'Adjust the score if the AI assessment is incorrect.'}
                    </p>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setOverrideScore(scorePercent);
                      setShowOverride(true);
                    }}
                  >
                    {isOverridden ? <><Undo2 className="w-4 h-4 mr-1" /> Re-Override</> : <><Edit3 className="w-4 h-4 mr-1" /> Override</>}
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  <h3 className="text-xs text-muted-foreground uppercase tracking-wider flex items-center gap-2">
                    <Edit3 className="w-4 h-4 text-orange-500" />
                    Override Score
                  </h3>
                  <div className="flex items-center gap-3">
                    <input
                      type="range"
                      min={0}
                      max={100}
                      value={overrideScore}
                      onChange={e => setOverrideScore(Number(e.target.value))}
                      className="flex-1 accent-sabio-yellow"
                    />
                    <span className="text-lg font-bold w-12 text-right">{overrideScore}%</span>
                  </div>
                  <textarea
                    value={overrideReason}
                    onChange={e => setOverrideReason(e.target.value)}
                    placeholder="Reason for override (required)…"
                    className="w-full rounded-lg border bg-background p-2 text-sm resize-none h-20 focus:outline-none focus:ring-2 focus:ring-sabio-yellow"
                  />
                  {overrideError && <p className="text-destructive text-sm">{overrideError}</p>}
                  <div className="flex gap-2 justify-end">
                    <Button variant="ghost" size="sm" onClick={() => { setShowOverride(false); setOverrideError(null); }}>
                      Cancel
                    </Button>
                    <Button
                      variant="sabio-yellow"
                      size="sm"
                      onClick={handleOverride}
                      disabled={overriding}
                    >
                      {overriding ? <><Loader2 className="w-4 h-4 animate-spin mr-1" /> Applying...</> : 'Apply Override'}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Alert Section */}
          {decision.is_relevant && (
            <div className="p-4 rounded-xl bg-muted/50 border">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-xs text-muted-foreground uppercase tracking-wider mb-1 flex items-center gap-2">
                    <Bell className="w-4 h-4 text-sabio-yellow" />
                    Alert Status
                  </h3>
                  {lastAlertedAt ? (
                    <p className="text-sm text-foreground/80">
                      Last alerted: <span className="text-primary font-medium">{formatRelativeTime(lastAlertedAt)}</span>
                    </p>
                  ) : (
                    <p className="text-sm text-muted-foreground">No alerts sent yet</p>
                  )}
                </div>
                <Button
                  variant="sabio-yellow"
                  size="sm"
                  onClick={handleTriggerAlert}
                  disabled={alerting}
                >
                  {alerting ? (
                    <><Loader2 className="w-4 h-4 animate-spin mr-1" /> Sending...</>
                  ) : (
                    <><Bell className="w-4 h-4 mr-1" /> {lastAlertedAt ? 'Re-Alert' : 'Send Alert'}</>
                  )}
                </Button>
              </div>
              {alertError && <p className="text-destructive text-sm mt-2">{alertError}</p>}
            </div>
          )}

          {/* Source Content */}
          {decision.event_url && (
            <div>
              <h3 className="text-xs text-muted-foreground uppercase tracking-wider mb-2 flex items-center gap-2">
                <ExternalLink className="w-4 h-4 text-primary" />
                Source Content
              </h3>
              <a
                href={decision.event_url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:underline text-sm mb-3 inline-block"
              >
                {decision.event_url}
              </a>

              {canShowIframe ? (
                <iframe
                  className="w-full h-72 rounded-xl border bg-white"
                  src={decision.event_url}
                  sandbox="allow-scripts allow-same-origin"
                  title="Source content"
                />
              ) : (
                <div className="w-full h-64 rounded-xl border bg-muted/30 flex flex-col items-center justify-center text-center p-8">
                  <div className="w-14 h-14 bg-primary/10 rounded-xl flex items-center justify-center mb-3">
                    <ShieldAlert className="w-7 h-7 text-primary" />
                  </div>
                  <p className="text-foreground/80 mb-1">This source doesn't allow embedded previews</p>
                  <p className="text-muted-foreground text-sm mb-4">Click the link above to view the original article</p>
                  <Button variant="sabio-yellow" size="sm" asChild>
                    <a href={decision.event_url} target="_blank" rel="noopener noreferrer">
                      Open in New Tab <ExternalLink className="w-4 h-4 ml-1" />
                    </a>
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

/** Horizontal bar showing a single scoring factor. */
function FactorBar({ label, value, weight }: { label: string; value: number; weight: string }) {
  const pct = Math.round(value * 100);
  const barColor = pct >= 70
    ? 'bg-green-500'
    : pct >= 50
      ? 'bg-sabio-yellow'
      : pct >= 30
        ? 'bg-orange-500'
        : 'bg-gray-400';
  return (
    <div className="space-y-1">
      <div className="flex justify-between text-xs">
        <span className="text-foreground/80">{label}</span>
        <span className="text-muted-foreground">{pct}% <span className="opacity-60">({weight})</span></span>
      </div>
      <div className="h-2 rounded-full bg-muted overflow-hidden">
        <div className={cn('h-full rounded-full transition-all', barColor)} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}
